/**
 * @fileoverview Reusable user avatar bubble with initials fallback.
 *
 * @purpose Show either the user's avatar or initials inside Ui elements like TopBar and SideMenu.
 * @usage Rendered by TopBar, SideMenu, and other shared panels.
 * @ownership Shared UI components (core)
 * @dependencies
 * - next/image
 * - react
 * - @/core/lib/utils (cn helper)
 * @restrictions
 * - DO NOT depend on session data here; pass props instead.
 */
'use client'
import Image from 'next/image'

import { cn } from '../../lib/utils'

export type UserAvatarBubbleProps = {
  name: string
  email?: string
  avatarUrl?: string | null
  size?: number
  className?: string
  'data-testid'?: string
}

export function UserAvatarBubble({
  name,
  email,
  avatarUrl,
  size = 32,
  className,
  'data-testid': qaIdentifier,
}: UserAvatarBubbleProps) {
  const fallbackSource = name || email || 'U'
  const initial = fallbackSource.charAt(0).toUpperCase()

  if (avatarUrl && avatarUrl.trim().length > 0) {
    return (
      <div
        className={cn('rounded-full overflow-hidden flex-shrink-0', className)}
        style={{ width: size, height: size }}
        data-testid={qaIdentifier}
      >
        <Image
          src={avatarUrl}
          alt={name || email || 'User'}
          width={size}
          height={size}
          className="h-full w-full object-cover rounded-full"
        />
      </div>
    )
  }

  return (
    <div
      className={cn(
        'flex-shrink-0 rounded-full bg-blue-600 flex items-center justify-center text-white font-semibold',
        className,
      )}
      style={{ width: size, height: size }}
      data-testid={qaIdentifier}
    >
      <span className="text-sm">{initial}</span>
    </div>
  )
}


